import {ChangeDetectionStrategy, Component, computed, inject, PLATFORM_ID} from '@angular/core';
import {PageService} from '@core/services/page.service';
import {environment} from '@env/environment';
import {isPlatformBrowser} from '@angular/common';
import {toSignal} from '@angular/core/rxjs-interop';

type CountryOption = {
  locale: string;
  countryCode: string;
  flagIcon: string;
  label: string;
  url: string | null;
};

@Component({
  selector: 'app-country-selector',
  templateUrl: './country-selector.component.html',
  styleUrls: ['./country-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true
})
export class CountrySelectorComponent {
  private pageService = inject(PageService);
  private platformId = inject(PLATFORM_ID);

  // Current locale from environment
  currentLocale = environment.locale;

  // Get page data as signal
  private pageData = toSignal(this.pageService.getPageAsync());

  // Parse alternate links from page head
  private alternateLinks = computed(() => {
    const page = this.pageData();
    if (!page?.page?.head?.link) {
      return [];
    }

    return page.page.head.link
      .filter(link => link?.rel === 'alternate' && link?.hreflang && link?.href)
      .map(link => ({
        hreflang: link.hreflang!,
        href: link.href!
      }));
  });

  // Available countries with their alternate URLs
  countries = computed<CountryOption[]>(() => {
    const alternates = this.alternateLinks();
    
    const countries: CountryOption[] = [
      {
        locale: 'da-DK',
        countryCode: 'DK',
        flagIcon: 'assets/country_icons/dk_round.svg',
        label: $localize`Supervin Danmark`,
        url: this.findAlternateUrl('da', alternates)
      },
      {
        locale: 'sv-SE',
        countryCode: 'SE',
        flagIcon: 'assets/country_icons/se_round.svg',
        label: $localize`Supervin Sverige`,
        url: this.findAlternateUrl('sv', alternates)
      }
    ];

    return countries;
  });

  // Current selected country
  currentCountry = computed(() => {
    return this.countries().find(c => c.locale === this.currentLocale);
  });

  // Other available countries
  otherCountries = computed(() => {
    return this.countries().filter(c => c.locale !== this.currentLocale);
  });

  /**
   * Find alternate URL for a specific language
   * Supports both full locale codes (e.g., 'da-DK') and language-only codes (e.g., 'da')
   */
  private findAlternateUrl(lang: string, alternates: Array<{hreflang: string, href: string}>): string | null {
    // First, try to find an exact match for the full locale (e.g., 'da-DK' matches 'da-DK')
    const exactLocaleMatch = alternates.find(alt => 
      alt.hreflang.toLowerCase() === lang.toLowerCase()
    );
    if (exactLocaleMatch) {
      return exactLocaleMatch.href;
    }
    
    // Second, try to find a match for the language part only (e.g., 'da' matches 'da-DK')
    const languageMatch = alternates.find(alt => 
      alt.hreflang.toLowerCase().startsWith(lang.toLowerCase() + '-')
    );
    if (languageMatch) {
      return languageMatch.href;
    }
    
    return null;
  }

  /**
   * Get homepage URL for a specific locale, preserving current hostname
   */
  private getHomepageForLocale(locale: string): string {
    if (!isPlatformBrowser(this.platformId)) {
      // Fallback for SSR - use production URLs
      return locale === 'sv-SE' ? 'https://www.supervin.se/' : 'https://www.supervin.dk/';
    }

    // Get current hostname and protocol
    const currentUrl = window.location;
    const protocol = currentUrl.protocol;
    let hostname = currentUrl.hostname;

    // Map current hostname to target hostname
    if (locale === 'sv-SE') {
      // Switch to Swedish equivalent
      if (hostname.includes('.dk.')) {
        hostname = hostname.replace('.dk.', '.se.');
      } else if (hostname === 'www.supervin.dk') {
        hostname = 'www.supervin.se';
      } else if (hostname.includes('dk-')) {
        hostname = hostname.replace('dk-', 'se-');
      } else {
        // If no clear mapping, use default
        hostname = environment.production ? 'www.supervin.se' : 'www.se.supervin.io';
      }
    } else {
      // Switch to Danish equivalent
      if (hostname.includes('.se.')) {
        hostname = hostname.replace('.se.', '.dk.');
      } else if (hostname === 'www.supervin.se') {
        hostname = 'www.supervin.dk';
      } else if (hostname.includes('se-')) {
        hostname = hostname.replace('se-', 'dk-');
      } else {
        // If no clear mapping, use default
        hostname = environment.production ? 'www.supervin.dk' : 'www.dk.supervin.io';
      }
    }

    return `${protocol}//${hostname}/`;
  }

  /**
   * Switch to a different country
   */
  switchCountry(country: CountryOption): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Always prefer the alternate URL from page metadata
    // Only fall back to constructed URL if no alternate exists
    const targetUrl = country.url || this.getHomepageForLocale(country.locale);
    
    // Navigate to target URL (full page reload to switch locale)
    window.location.href = targetUrl;
  }
}
